import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api/axiosConfig';
import { useModal } from '../contexts/ModalContext';
import { money } from '../utils/money';
import AuthContext from '../contexts/AuthContext';
import { fmtDate } from '../utils/formatDate';

const STATE = {
    SCHEDULED: { text: '예정', bg: '#e0e7ff', color: '#4338ca' },
    ACTIVE: { text: '임대 중', bg: '#d1fae5', color: '#059669' },
    EXPIRED: { text: '만료', bg: '#f3f4f6', color: '#6b7280' },
    TERMINATED: { text: '해지', bg: '#fee2e2', color: '#dc2626' },
};


/** 만료가 코앞인가. 두 달을 남기고부터 눈에 띄게 한다. */
const SOON_DAYS = 60;

/* 계약 이야기는 '석 달 전' 이라고 하지 '90일 전' 이라고 하지 않는다. 서버는
   날로 세고, 화면에서만 달과 주로 부른다. */
const START_CHOICES = [
    { days: 90, label: '3개월' },
    { days: 180, label: '6개월' },
    { days: 270, label: '9개월' },
    { days: 365, label: '1년' },
];
const EVERY_CHOICES = [
    { days: 7, label: '1주' },
    { days: 14, label: '2주' },
    { days: 30, label: '1개월' },
];
const labelOf = (choices, days) =>
    choices.find((c) => c.days === days)?.label ?? `${days}일`;

/**
 * 임대차 계약 목록.
 *
 * 끝난 계약도 남긴다. '이 호실에 누가 있었나' 는 지난 일을 되짚을 때 가장 먼저
 * 묻는 것이고, 목록에서 지워 버리면 물어볼 데가 없다. 대신 기본은 진행 중인
 * 것만 보여준다 -- 오늘 할 일과 지난 일이 한 줄에 섞이면 훑을 수가 없다.
 */
const LeaseListPage = () => {
    const navigate = useNavigate();
    const [leases, setLeases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showEnded, setShowEnded] = useState(false);
    const [notice, setNotice] = useState(null);
    const [editingNotice, setEditingNotice] = useState(false);
    const { showAlert } = useModal();
    const { user } = useContext(AuthContext);
    // 건물주는 자기 건물의 계약을 본다. 고치는 것은 관리하는 쪽의 일이라
    // 서버가 막고, 화면에서도 누를 것을 내지 않는다 -- 눌러 보고 막히는 것과
    // 처음부터 없는 것은 다르다.
    const readOnly = Boolean(user?.clientId);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/leases');
            setLeases(data || []);
            setError(null);
        } catch (err) {
            console.error('Failed to load leases', err);
            setError(
                err.response?.status === 403
                    ? '임대차를 볼 권한이 없습니다.'
                    : '계약을 불러오지 못했습니다.',
            );
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        // 알림 주기는 회사가 정하는 값이라 건물주에게는 묻지 않는다.
        if (readOnly) return undefined;
        let alive = true;
        api
            .get('/leases/notice-settings')
            .then((res) => alive && setNotice(res.data))
            .catch((err) => console.error('Failed to load notice settings', err));
        return () => {
            alive = false;
        };
    }, [readOnly]);

    const saveNotice = async (patch) => {
        const next = { ...notice, ...patch };
        setNotice(next);
        try {
            await api.patch('/leases/notice-settings', next);
        } catch (err) {
            showAlert('오류', err.response?.data?.message || '저장하지 못했습니다.', 'danger');
        }
    };

    const shown = useMemo(
        () =>
            showEnded
                ? leases
                : leases.filter((l) => l.status === 'ACTIVE' || l.status === 'SCHEDULED'),
        [leases, showEnded],
    );

    const endedCount = leases.length - shown.length;
    const soon = leases.filter(
        (l) => l.daysLeft !== null && l.daysLeft <= SOON_DAYS,
    ).length;

    return (
        <div className="pb-5">
            <div className="d-flex justify-content-between align-items-start gap-2 mb-3 flex-wrap">
                <div>
                    <h4 className="fw-bold mb-1">임대차 계약</h4>
                    <p className="text-muted small mb-0">누가 어느 호실을 언제까지 쓰는지.</p>
                </div>
                {!readOnly && (
                    <Link to="/leases/new" className="btn btn-primary fw-bold px-3">
                        <i className="bi bi-plus-lg"></i>
                        <span className="d-none d-md-inline ms-2">계약 등록</span>
                    </Link>
                )}
            </div>

            {error ? (
                <div className="alert alert-warning">{error}</div>
            ) : loading ? (
                <div className="text-center p-5">
                    <div className="spinner-border text-primary" role="status"></div>
                </div>
            ) : (
                <>
                    {soon > 0 && (
                        <div className="bg-white rounded-4 border p-3 mb-3">
                            <i className="bi bi-hourglass-split text-warning me-2"></i>
                            <span className="small">
                                {SOON_DAYS}일 안에 끝나는 계약이 <strong>{soon}건</strong> 있습니다.
                            </span>
                        </div>
                    )}

                    {/* 알림이 언제 오는지 계약 목록에서 바로 보이고 바꿀 수 있게
                        둔다. 설정 화면으로 옮기면 이걸 신경 쓰는 사람이 가장 덜
                        가는 곳에 놓이는 셈이다. */}
                    {notice && (
                        <div className="bg-white rounded-4 border p-3 mb-3">
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                <i className="bi bi-bell text-secondary"></i>
                                <span className="small">
                                    만료 <strong>{labelOf(START_CHOICES, notice.startDays)}</strong> 전부터{' '}
                                    <strong>{labelOf(EVERY_CHOICES, notice.everyDays)}</strong>마다 알립니다.
                                </span>
                                <button
                                    className="btn btn-sm border-0 p-0 text-primary ms-auto"
                                    style={{ fontSize: '0.8rem' }}
                                    onClick={() => setEditingNotice(!editingNotice)}
                                >
                                    {editingNotice ? '닫기' : '바꾸기'}
                                </button>
                            </div>

                            {editingNotice && (
                                <div className="mt-3">
                                    <div className="text-muted mb-1" style={{ fontSize: '0.75rem' }}>
                                        언제부터
                                    </div>
                                    <div className="btn-group btn-group-sm mb-3 flex-wrap" role="group">
                                        {START_CHOICES.map((c) => (
                                            <button
                                                key={c.days}
                                                className={`btn ${notice.startDays === c.days ? 'btn-primary' : 'btn-white border'}`}
                                                onClick={() => saveNotice({ startDays: c.days })}
                                            >
                                                {c.label}
                                            </button>
                                        ))}
                                    </div>
                                    <div className="text-muted mb-1" style={{ fontSize: '0.75rem' }}>
                                        얼마마다
                                    </div>
                                    <div className="btn-group btn-group-sm flex-wrap" role="group">
                                        {EVERY_CHOICES.map((c) => (
                                            <button
                                                key={c.days}
                                                className={`btn ${notice.everyDays === c.days ? 'btn-primary' : 'btn-white border'}`}
                                                onClick={() => saveNotice({ everyDays: c.days })}
                                            >
                                                {c.label}
                                            </button>
                                        ))}
                                    </div>
                                    {/* 마지막 날은 간격과 상관없이 온다는 것을 적어 둔다.
                                        1개월마다로 두면 마지막에 조용할까 걱정하게 된다. */}
                                    <div className="text-muted mt-2" style={{ fontSize: '0.72rem' }}>
                                        만료 당일에는 간격과 상관없이 한 번 더 알립니다. 소유자·최고 관리자와
                                        「임대차 관리」 권한을 받은 사람이 받습니다.
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {shown.length === 0 ? (
                        <div className="bg-white rounded-4 border text-center p-5">
                            <i className="bi bi-file-earmark-text display-6 d-block mb-3 text-muted opacity-50"></i>
                            <h6 className="fw-bold mb-1">계약이 없습니다</h6>
                            <p className="text-muted small mb-0">
                                {endedCount > 0
                                    ? '진행 중인 계약이 없습니다. 아래에서 끝난 계약을 볼 수 있습니다.'
                                    : readOnly
                                      ? '등록된 계약이 없습니다.'
                                      : '호실을 먼저 등록한 뒤 계약을 더해 주세요.'}
                            </p>
                            {/* 호실은 시설마다 있어서 여기서 바로 만들 수 없다.
                                고르는 데까지는 데려다준다. */}
                            {endedCount === 0 && !readOnly && (
                                <Link to="/facilities" className="small fw-bold text-decoration-none">
                                    시설 고르러 가기
                                    <i className="bi bi-chevron-right ms-1" style={{ fontSize: '0.7rem' }}></i>
                                </Link>
                            )}
                        </div>
                    ) : (
                        shown.map((l) => {
                            const s = STATE[l.status] || STATE.EXPIRED;
                            const urgent = l.daysLeft !== null && l.daysLeft <= SOON_DAYS;
                            return (
                                <div
                                    key={l.id}
                                    className="bg-white rounded-4 border p-3 mb-2"
                                    style={{ cursor: readOnly ? 'default' : 'pointer' }}
                                    onClick={
                                        readOnly ? undefined : () => navigate(`/leases/${l.id}`)
                                    }
                                >
                                    <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                                        <span className="fw-bold">{l.tenant?.name}</span>
                                        <span
                                            className="badge rounded-pill fw-normal"
                                            style={{ background: s.bg, color: s.color }}
                                        >
                                            {s.text}
                                        </span>
                                        <span
                                            className={`ms-auto text-nowrap ${urgent ? 'text-danger fw-bold' : 'text-muted'}`}
                                            style={{ fontSize: '0.78rem' }}
                                        >
                                            {l.daysLeft !== null
                                                ? `${l.daysLeft}일 남음`
                                                : `~ ${fmtDate(l.endDate)}`}
                                        </span>
                                    </div>
                                    <div className="text-muted" style={{ fontSize: '0.78rem' }}>
                                        {/* 한 계약이 여러 호실을 쓴다. 층을 통째로 빌린 임차인이 그렇다. */}
                                        {(l.units || [])
                                            .map((u) => `${u.facility?.name ?? ''} ${u.name}`.trim())
                                            .join(' · ') || '호실 없음'}
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                        <div className="text-muted flex-grow-1" style={{ fontSize: '0.75rem' }}>
                                            {fmtDate(l.startDate)} ~ {fmtDate(l.endDate)}
                                            {money(l.monthlyRent) && ` · 월 ${money(l.monthlyRent)}`}
                                        </div>
                                        {/* 끝이 보이는 계약에만 낸다. 이제 막 시작한
                                            계약 옆에 있어 봐야 누를 일이 없다. */}
                                        {!readOnly && (urgent || l.status === 'EXPIRED') && (
                                            <button
                                                className="btn btn-sm btn-white border rounded-pill px-3 text-nowrap"
                                                style={{ fontSize: '0.75rem' }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate(`/leases/new?from=${l.id}`);
                                                }}
                                            >
                                                재계약
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    )}

                    {endedCount > 0 && !showEnded && (
                        <button
                            className="btn btn-white border rounded-pill px-3 w-100 mt-2"
                            style={{ fontSize: '0.82rem' }}
                            onClick={() => setShowEnded(true)}
                        >
                            끝난 계약 {endedCount}건 보기
                        </button>
                    )}
                </>
            )}
        </div>
    );
};

export default LeaseListPage;
